import React, { createContext, useContext, useReducer, useMemo, useCallback } from 'react'

// Action types para el UI context
export const UI_ACTIONS = {
  SHOW_MODAL: 'SHOW_MODAL',
  HIDE_MODAL: 'HIDE_MODAL',
  SET_LOADING: 'SET_LOADING',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION',
  UPDATE_CURSOR_POSITION: 'UPDATE_CURSOR_POSITION',
  SET_DRAG_STATE: 'SET_DRAG_STATE',
  TOGGLE_MENU: 'TOGGLE_MENU',
  SET_FOCUS_ELEMENT: 'SET_FOCUS_ELEMENT'
}

// Estado inicial del UI context
const initialUIState = {
  modal: {
    isOpen: false,
    type: null,
    props: null
  },
  loading: {
    isLoading: false,
    message: ''
  },
  notification: {
    message: '',
    type: 'info', // 'info', 'success', 'warning', 'error'
    isVisible: false,
    timeout: 3000
  },
  cursor: {
    x: 0,
    y: 0
  },
  dragState: {
    isDragging: false,
    item: null,
    startPosition: { x: 0, y: 0 }
  },
  menu: {
    isOpen: false,
    activeMenu: null
  },
  focus: {
    elementId: null,
    type: null
  }
}

// Reducer para el UI context
function uiReducer(state, action) {
  switch (action.type) {
    case UI_ACTIONS.SHOW_MODAL:
      return {
        ...state,
        modal: {
          isOpen: true,
          type: action.payload.type,
          props: action.payload.props || null
        }
      }
    
    case UI_ACTIONS.HIDE_MODAL:
      return {
        ...state,
        modal: {
          isOpen: false,
          type: null,
          props: null
        }
      }
    
    case UI_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: {
          isLoading: action.payload.isLoading,
          message: action.payload.message || ''
        }
      }
    
    case UI_ACTIONS.SET_NOTIFICATION:
      return {
        ...state,
        notification: {
          message: action.payload.message,
          type: action.payload.type || 'info',
          isVisible: true,
          timeout: action.payload.timeout || 3000
        }
      }
    
    case UI_ACTIONS.CLEAR_NOTIFICATION:
      return {
        ...state,
        notification: {
          ...state.notification,
          isVisible: false
        }
      }
    
    case UI_ACTIONS.UPDATE_CURSOR_POSITION:
      return {
        ...state,
        cursor: {
          x: action.payload.x,
          y: action.payload.y
        }
      }
    
    case UI_ACTIONS.SET_DRAG_STATE:
      return {
        ...state,
        dragState: {
          isDragging: action.payload.isDragging,
          item: action.payload.item || null,
          startPosition: action.payload.startPosition || { x: 0, y: 0 }
        }
      }
    
    case UI_ACTIONS.TOGGLE_MENU:
      return {
        ...state,
        menu: {
          isOpen: !state.menu.isOpen,
          activeMenu: action.payload?.menu || state.menu.activeMenu
        }
      }
    
    case UI_ACTIONS.SET_FOCUS_ELEMENT:
      return {
        ...state,
        focus: {
          elementId: action.payload.elementId,
          type: action.payload.type
        }
      }
    
    default:
      return state
  }
}

// Crear el contexto de UI
const UIContext = createContext(null)

// Hook personalizado para usar el contexto de UI
export const useUIContext = () => {
  const context = useContext(UIContext)
  if (!context) {
    throw new Error('useUIContext debe ser usado dentro de UIContextProvider')
  }
  return context
}

// Provider del contexto de UI
export const UIContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(uiReducer, initialUIState)

  // Action creators optimizados
  const showModal = useCallback((type, props) => 
    dispatch({ type: UI_ACTIONS.SHOW_MODAL, payload: { type, props } }), [])
  
  const hideModal = useCallback(() => 
    dispatch({ type: UI_ACTIONS.HIDE_MODAL }), [])
  
  const setLoading = useCallback((isLoading, message = '') => 
    dispatch({ type: UI_ACTIONS.SET_LOADING, payload: { isLoading, message } }), [])
  
  const showNotification = useCallback((message, type = 'info', timeout = 3000) => 
    dispatch({ type: UI_ACTIONS.SET_NOTIFICATION, payload: { message, type, timeout } }), [])
  
  const clearNotification = useCallback(() => 
    dispatch({ type: UI_ACTIONS.CLEAR_NOTIFICATION }), [])
  
  const updateCursorPosition = useCallback((x, y) => 
    dispatch({ type: UI_ACTIONS.UPDATE_CURSOR_POSITION, payload: { x, y } }), [])
  
  const setDragState = useCallback((isDragging, item = null, startPosition = { x: 0, y: 0 }) => 
    dispatch({ type: UI_ACTIONS.SET_DRAG_STATE, payload: { isDragging, item, startPosition } }), [])
  
  const toggleMenu = useCallback((menu = null) => 
    dispatch({ type: UI_ACTIONS.TOGGLE_MENU, payload: { menu } }), [])
  
  const setFocusElement = useCallback((elementId, type = null) => 
    dispatch({ type: UI_ACTIONS.SET_FOCUS_ELEMENT, payload: { elementId, type } }), [])

  const actions = useMemo(() => ({
    showModal,
    hideModal,
    setLoading,
    showNotification,
    clearNotification,
    updateCursorPosition,
    setDragState,
    toggleMenu,
    setFocusElement
  }), [showModal, hideModal, setLoading, showNotification, clearNotification, updateCursorPosition, setDragState, toggleMenu, setFocusElement])

  // Valor del contexto memoizado
  const contextValue = useMemo(() => ({
    ...state,
    actions
  }), [state, actions])

  return (
    <UIContext.Provider value={contextValue}>
      {children}
    </UIContext.Provider>
  )
}

export default UIContext